import React, { useCallback, useLayoutEffect } from 'react';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  type AddressType,
  IconLink,
  IllusDressing_1,
  ModalCloseBtn,
  theme,
  useResponsive,
} from '@sg/garnish';
import { OrderType } from '@sg/graphql-schema';

import { useMomoSurveyUrl } from '@order/features-order-status';

import { LoadingAnimation, SignedOutView, TransparentModal } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCustomer } from '../../context/Customer';
import { useDerivedRateOrderStatus } from './hooks';
import { useRateOrderNavigation } from './RateOrder.navigation';
import { useRateOrder } from './RateOrderScreen.data';
import { LandingContent } from './RateOrderScreen.landing';
import { MomosSurveyContent } from './RateOrderScreen.momos';
import { RatingContent, RatingWithTitle } from './RateOrderScreen.rating';

// ─────────────────────────────────────────────────────────────────────────────

export const RateOrderScreen = () => {
  const { dismissRateOrder } = useRateOrderNavigation();
  const isLoggedIn = useIsLoggedIn();
  const { orderId, isInStoreOrder: isInStoreOrderParam } = useLocalSearchParams<{
    orderId: string;
    isInStoreOrder?: 'true' | 'false';
  }>();
  const { customer } = useCustomer();

  const { minWidth, match } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Navigation Helpers ──────────────────────────────────────────────

  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  const renderNavigationHeaderRight = useCallback(
    () => (
      <IconLink
        width={36}
        height={36}
        name="IconClose"
        palette="muted"
        accessibilityLabel={formatMessage(messages.rateOrderClose)}
        onPress={dismissRateOrder}
      />
    ),
    [dismissRateOrder, formatMessage],
  );

  useLayoutEffect(() => {
    setOptions({ headerRight: renderNavigationHeaderRight });
  }, [renderNavigationHeaderRight, setOptions]);

  // ─── Derived Order Data ──────────────────────────────────────────────

  const orderTypeIsInStore = isInStoreOrderParam === 'true';

  const { isFetching, isInvalidOrder, isPendingOrder, isRatedOrder, order } =
    useDerivedRateOrderStatus(orderId, orderTypeIsInStore);

  const orderType = order?.orderType;

  const momoSurveyUrl = useMomoSurveyUrl(
    customer,
    order
      ? {
          orderId,
          orderTime: order.wantedTime,
          orderType: toAddressType(orderType),
          restaurantEntityId: order.restaurantEntityId,
        }
      : undefined,
  );

  const showMomosSurvey = !!momoSurveyUrl && !isFetching && !isInvalidOrder && !isPendingOrder;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      size={match(['full', showMomosSurvey ? 'large' : 'medium'])}
      dialogMaxHeight={showMomosSurvey ? MOMOS_MODAL_MAX_HEIGHT : undefined}
      dialogMaxWidth={showMomosSurvey ? MOMOS_MODAL_MAX_WIDTH : undefined}
      accessibilityLabel={
        showMomosSurvey ? formatMessage(messages.rateOrderMomosSurveyModal) : undefined
      }
      onClose={dismissRateOrder}
    >
      {minWidth.isSM ? <ModalCloseBtn onPress={dismissRateOrder} /> : null}

      {isLoggedIn ? (
        <RateOrderScreenSignedInContent
          isFetching={isFetching}
          isInvalidOrder={isInvalidOrder}
          isPendingOrder={isPendingOrder}
          isRatedOrder={isRatedOrder}
          momoSurveyUrl={momoSurveyUrl}
          orderId={orderId}
          orderType={orderType ?? null}
          onMomoSurveyCompleted={dismissRateOrder}
        />
      ) : (
        <RateOrderScreenSignedOutContent />
      )}
    </TransparentModal>
  );
};

const MOMOS_MODAL_MAX_HEIGHT = 720;
const MOMOS_MODAL_MAX_WIDTH = 600;

// ─── Components ──────────────────────────────────────────────────────────────

const RateOrderScreenSignedOutContent = () => {
  return <SignedOutView redirectTo="/account/orders" withoutFooter />;
};

type RateOrderScreenSignedInContentProps = Readonly<{
  isFetching: boolean;
  isInvalidOrder: boolean;
  isPendingOrder: boolean;
  isRatedOrder: boolean;
  momoSurveyUrl: string | null;
  orderId: string;
  orderType: OrderType | null;
  onMomoSurveyCompleted: () => void;
}>;

const RateOrderScreenSignedInContent = ({
  isFetching,
  isInvalidOrder,
  isPendingOrder,
  isRatedOrder,
  momoSurveyUrl,
  orderId,
  orderType,
  onMomoSurveyCompleted,
}: RateOrderScreenSignedInContentProps) => {
  const { formatMessage } = useIntl();

  if (isFetching) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation />
      </View>
    );
  }

  if (isInvalidOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration
          source={IllusDressing_1}
          style={styles.notFoundViewIllustration}
        />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderNotFoundOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderNotFoundOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  if (isPendingOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderPendingOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderPendingOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  if (isRatedOrder) {
    return (
      <LandingContent>
        <LandingContent.Illustration />

        <LandingContent.Title>
          {formatMessage(messages.rateOrderRatedOrderViewTitle)}
        </LandingContent.Title>

        <LandingContent.Subtitle>
          {formatMessage(messages.rateOrderRatedOrderViewSubtitle)}
        </LandingContent.Subtitle>
      </LandingContent>
    );
  }

  return momoSurveyUrl && orderType ? (
    <MomosSurveyContent
      url={momoSurveyUrl}
      orderId={orderId}
      orderType={orderType}
      onCompleted={onMomoSurveyCompleted}
    />
  ) : (
    <RateOrderScreenSignedInContentRating />
  );
};

const RateOrderScreenSignedInContentRating = () => {
  const {
    loading,
    submitting,
    landingTitle,
    landingSubtitle,
    ratingTitle,
    ratingSubtitle,
    ratingHint,
    ratingCta,
    cards,
    rating,
    handleHoverRating,
    handleRating,
    handleCardSelection,
    handleSubmit,
  } = useRateOrder();

  // ─────────────────────────────────────────────

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation />
      </View>
    );
  }

  return (
    <>
      {rating === 0 && landingTitle ? (
        <LandingContent>
          <LandingContent.Illustration />

          <LandingContent.Title>{landingTitle}</LandingContent.Title>

          <LandingContent.Subtitle>{landingSubtitle}</LandingContent.Subtitle>

          <RatingWithTitle
            rating={rating}
            ratingTitle={ratingTitle}
            onRating={handleRating}
            onHoverRating={handleHoverRating}
          />
        </LandingContent>
      ) : null}

      {rating > 0 && ratingSubtitle ? (
        <RatingContent
          ratingTitle={ratingTitle}
          ratingSubtitle={ratingSubtitle}
          ratingHint={ratingHint}
          ratingCta={ratingCta}
          cards={cards}
          rating={rating}
          submitting={submitting}
          onHoverRating={handleHoverRating}
          onRating={handleRating}
          onSelectCard={handleCardSelection}
          onSubmit={handleSubmit}
        />
      ) : null}
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toAddressType = (orderType?: OrderType | null): AddressType | undefined => {
  switch (orderType) {
    case OrderType.Delivery:
      return 'delivery';
    case OrderType.InStore:
      return 'in-store';
    case OrderType.Pickup:
      return 'pickup';
    default:
      return undefined;
  }
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  rateOrderClose: {
    defaultMessage: 'Close',
    description: 'Rate Order | Close',
  },
  rateOrderNotFoundOrderViewTitle: {
    defaultMessage: `We couldn't find this order.`,
    description: 'Rate | Order | Not Found Order View | Title',
  },
  rateOrderNotFoundOrderViewSubtitle: {
    defaultMessage: 'Please select another order to rate.',
    description: 'Rate | Order | Not Found Order View | Subtitle',
  },
  rateOrderPendingOrderViewTitle: {
    defaultMessage: 'This order is still in progress.',
    description: 'Rate | Order | Pending Order View | Title',
  },
  rateOrderPendingOrderViewSubtitle: {
    defaultMessage: 'Come back to rate this order once it has been completed.',
    description: 'Rate | Order | Pending Order View | Subtitle',
  },
  rateOrderRatedOrderViewTitle: {
    defaultMessage: 'You’ve already rated this order.',
    description: 'Rate | Order | Rated Order View | Title',
  },
  rateOrderRatedOrderViewSubtitle: {
    defaultMessage: 'Thank you! Your feedback means a lot.',
    description: 'Rate | Order | Rated Order View | Subtitle',
  },
  rateOrderMomosSurveyModal: {
    defaultMessage: 'Rate order survey',
    description: 'Rate | Order | Momos Survey | Modal Accessibility Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    paddingTop: theme.spacing['10'],
    paddingBottom: theme.spacing['20'],
  },
  notFoundViewIllustration: {
    width: 200,
    height: 200,
  },
});
